import { useFormik, Form as FormikForm, FormikProvider } from "formik"
import { useCallback, useRef, useState } from "react"
import { Button, Form, Image, Modal } from "react-bootstrap"
import { toast } from "react-toastify"
import { AssessmentQuestion, updateAssessmentQuestion } from "shared/routes/assignments"
import SelectStandards from "../Standards/AssessmentQuestionStandards"
import { StringBoolean } from "shared/types/commonTypes"
import { sanitizePath } from "shared/helpers/moduleHelper"
import { convertFileToBase64, convertImgToBase64 } from "components/Assignment/helpers/drawingHelper"
import { BeatLoader } from "react-spinners"

interface Props {
    show: boolean
    hide: () => void
    totalQuestionsCount: number
    index: number
    assessmentId: string
    moduleName: string
    type: string
    assessmentNumber: number
    question: AssessmentQuestion
    questionImagePath: string | null
    refreshQuestions: () => void
}

type AssessmentQuestionAnswer = AssessmentQuestion["answers"][number]

export interface EditingAssessmentQuestionProps {
    questionText: string
    assessmentQuestionImage: File | string
    questionImageAltText: string
    position: number
    isDrawing: StringBoolean
    randomizable: boolean
    answers: AssessmentQuestionAnswer[]
    correctAnswer: string
    explanation: string
    subtopicIds: string[]
}

const getImagePath = (moduleName: string, type: string, assessmentNumber: number, questionNumber: number, letter: string) => {
    return `/simulations/${sanitizePath(moduleName)}/assessment/${type.toLowerCase()}-assessment-${assessmentNumber}-question-${questionNumber}-answer-${letter.toLowerCase()}.png`
}

const EditAssessmentQuestion = ({ show, hide, totalQuestionsCount, index, assessmentId, moduleName, type, assessmentNumber, question, questionImagePath, refreshQuestions } : Props) => {
    const questionImageRef = useRef<HTMLInputElement>(null)
    const [submitting, setSubmitting] = useState(false)

    const onSubmit = useCallback(async (values: EditingAssessmentQuestionProps) => {
        if (values.answers.length === 0) return toast.error("You must have at least one answer for the question.")
        if (values.correctAnswer === undefined) return toast.error("You must select a correct answer for the question.")

        /** Convert existing answer images to base64 string and include it for the backend */    
        const answers = await Promise.all(values.answers.map(async a => {
            if (a.imageExists && a.image?.letter && a.image?.update) {
                const answerImagePath = getImagePath(moduleName, type, assessmentNumber, question.number, a.image.letter)
                const base64String = (await convertImgToBase64(answerImagePath)).replace(/^data:image\/\w+;base64,/, "")
                return {answer: base64String, explanation: a.explanation}
            }
            return {answer: a.answer, explanation: a.explanation}
        }))

        if (!values.answers.map(a => a.letter).includes(values.correctAnswer)) return toast.error("Please ensure the correct answer is one of the provided answers.")

        const props = {
            assessmentId: assessmentId,
            questionNumber: question.number,
            questionText: values.questionText,
            assessmentQuestionImage: values.assessmentQuestionImage,
            questionImageAltText: values.questionImageAltText,
            correctAnswer: values.correctAnswer,
            answers: JSON.stringify(answers),
            answersImageAltTexts: JSON.stringify(values.answers.map(a => a.image?.altText ?? "")),
            explanation: values.explanation,
            isDrawing: values.isDrawing,
            randomizable: values.randomizable,
            position: values.position,
            subtopicIds: JSON.stringify(values.subtopicIds ?? []),
        }

        try {
            await updateAssessmentQuestion(props)
            toast.success("Question updated successfully.")
            setSubmitting(true)

            // wait for 1.5 seconds to allow the image to be properly loaded
            setTimeout(() => {
                refreshQuestions()
                window.location.reload()
            }, 1500)
        } catch {
            toast.error("There was something wrong updating the question. Please try again.")
        }
    }, [assessmentId, assessmentNumber, moduleName, type, question.number, refreshQuestions])

    const formik = useFormik({
        initialValues: {
            questionText: question.question ?? "",
            assessmentQuestionImage: undefined,
            questionImageAltText: question.image?.altText ?? undefined,
            position: index + 1,
            isDrawing: question.isDrawing,
            randomizable: question.randomizable,
            answers: question.answers ?? [],
            correctAnswer: question.correctAnswer ?? "",
            explanation: question.explanation ?? "",
            subtopicIds: undefined,
        },
        onSubmit: onSubmit,
    })

    const { values, setFieldValue, handleChange } = formik

    const onHideModal = useCallback(() => {
        hide()
        formik.resetForm()
    }, [hide, formik])

    const removeQuestionImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (questionImageRef.current.files?.length > 0) {
            // only remove if no new image is currently uploaded
            toast.error("An image is currently uploaded.")
            return
        }
        setFieldValue("assessmentQuestionImage", e.target.checked ? null : undefined)
    }, [setFieldValue])

    const deleteQuestionImageFileSelection = useCallback(() => {
        if (questionImageRef.current?.files?.length === 0) return
        questionImageRef.current.value = ""
        setFieldValue("assessmentQuestionImage", undefined)
    }, [setFieldValue])

    const onChangeImageAnswer = useCallback(async (e: HTMLInputElement, letter: string) => {
        const imageFile = e.files?.[0]
        const base64String = imageFile ? await convertFileToBase64(imageFile) : null
        setFieldValue("answers", values.answers.map(a => {
            if (a.letter === letter) return { ...a, answer: base64String.replace(/^data:image\/\w+;base64,/, "") }
            return a
        }))
    }, [setFieldValue, values.answers])

    const onChangeImageAnswerAltText = useCallback(async (e: HTMLInputElement, letter: string) => {
        setFieldValue("answers", values.answers.map(a => {
            if (a.letter === letter) return { ...a, image: { ...a.image, altText: e.value } }
            return a
        }))
    }, [setFieldValue, values.answers])

    const onChangeAnswerExplanation = useCallback(async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, letter: string) => {
        setFieldValue("answers", values.answers.map(a => {
            if (a.letter === letter) return { ...a, explanation: e.target.value }
            return a
        }))
    }, [setFieldValue, values.answers])

    const onChangeTextAnswer = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, letter: string) => {
        setFieldValue("answers", values.answers.map(a => {
            if (a.letter === letter) return { ...a, answer: e.target.value }
            return a
        }))
    }, [setFieldValue, values.answers])

    const addNewAnswer = useCallback(async (answerType: "text" | "image") => {
        const newLetter = String.fromCharCode((values.isDrawing === "Yes" ? 0 : values.answers.length) + 65)
        if (answerType === "text") {
            const newAnswer: AssessmentQuestionAnswer = { answer: "", letter: newLetter, originalLetter: newLetter, image: { letter: null, altText: null, update: null }, imageExists: false }
            setFieldValue("answers", values.isDrawing === "Yes" ? [newAnswer] : values.answers.concat(newAnswer))
        } else {
            const newAnswer: AssessmentQuestionAnswer = { answer: "", letter: newLetter, originalLetter: newLetter, image: null, imageExists: true }
            setFieldValue("answers", values.isDrawing === "Yes" ? [newAnswer] : values.answers.concat(newAnswer))
        }
    }, [setFieldValue, values.isDrawing, values.answers])

    const deleteAnswer = useCallback((letter: string) => {
        const filteredAnswers = values.answers.filter(a => a.letter !== letter)
        setFieldValue("answers", filteredAnswers.map(a => ({...a, letter: String.fromCharCode(filteredAnswers.indexOf(a) + 65) })))
        if (values.correctAnswer === letter) {
            setFieldValue("correctAnswer", undefined)
        }
    }, [setFieldValue, values.answers, values.correctAnswer])

    const updateQuestionType = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.checked) toast.info("Original answers have been restored.")
        setFieldValue("answers", e.target.checked ? [] : question.answers)
        setFieldValue("isDrawing", e.target.checked === true ? "Yes" : "No")
        setFieldValue("correctAnswer", undefined)
    }, [setFieldValue, question.answers])

  return (
    <FormikProvider value={formik}>
        <Form as={FormikForm}>
            <Modal show={show} onHide={onHideModal} size="xl" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Assessment Question</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="assessmentQuestion" className="my-3 text-start">
                        <Form.Label className="h4 fw-bold">Question</Form.Label>
                        <Form.Control as="textarea" name="questionText" rows={10} value={values.questionText} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group controlId="image" className="my-3 text-start">
                        <Form.Label className="h4 fw-bold">Question Image</Form.Label>
                        {question.imageExists && 
                            <div className="d-flex flex-column align-items-center mb-3">
                                <Image
                                    role="button"
                                    src={questionImagePath}
                                    alt="Assessment Question Image"
                                    className={`d-block rounded text-center shadow-sm border border-1 my-3`} />
                                <Form.Check
                                        inline
                                        type="checkbox"
                                        className="me-2 text-danger fw-bold"
                                        label="Remove Image"
                                        onChange={removeQuestionImage}
                                    />
                            </div>} 
                        <Form.Control 
                            type="file"
                            ref={questionImageRef}
                            name="assessmentQuestionImage" 
                            accept=".png,image/png,.jpg,.jpeg,image/jpeg"
                            onChange={(e) => {
                                const element = e.target as HTMLInputElement
                                setFieldValue("assessmentQuestionImage", element.files[0])
                            }} />
                        <Button variant="danger" className="mt-2" onClick={deleteQuestionImageFileSelection}>Delete File Selection</Button>
                        <Form.Control 
                            type="text" 
                            name="questionImageAltText"
                            className="mt-2"
                            placeholder="Alt text - brief description for screen readers" 
                            value={values.questionImageAltText ?? ""}
                            onChange={handleChange} />
                    </Form.Group>

                    <Form.Group controlId="position" className="my-3 text-start fw-bold">
                        <Form.Label className="h4 fw-bold">Position</Form.Label>
                        <Form.Select aria-label="Question Position Select" name="position" onChange={handleChange}>
                            {Array.from({ length: totalQuestionsCount }, (_, i) => i + 1).map(pos => (
                                <option value={pos} selected={index + 1 === pos}>{pos}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="isDrawing" className="my-4 text-start h4 fw-bold">
                        <Form.Check
                            name="isDrawing"
                            onChange={updateQuestionType}
                            checked={values.isDrawing === "Yes"}
                            type="switch"
                            id="drawing-question-switch"
                            label="Is Drawing Question?"
                        />
                    </Form.Group>

                    <Form.Group controlId="randomizable" className="my-4 text-start h4 fw-bold">
                        <Form.Check
                            name="randomizable"
                            onChange={e => setFieldValue("randomizable", e.target.checked)}
                            checked={values.randomizable === true}
                            type="switch"
                            id="randomizable-question-switch"
                            label="Is Randomizable?"
                        />
                    </Form.Group>

                    <Form.Group controlId="correctAnswer" className="mb-4 text-start">
                        <Form.Label className="h4 fw-bold">Correct Answer</Form.Label>
                        <Form.Select aria-label="Correct Answer Select" name="correctAnswer" onChange={handleChange}>
                            <option value={undefined} selected={values.correctAnswer === undefined} disabled>N/A</option>
                            {values.answers.map(answer => (
                                <option value={answer.letter} selected={answer.letter === values.correctAnswer}>{answer.letter}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="answers" className="mb-4 text-start">
                        <Form.Label className="fw-bold h4">Answers</Form.Label>
                        {values.answers.length === 0 && <p className="text-muted">There are no answers for this question.</p>}
                        {values.isDrawing === "Yes" && <p className="text-muted">Drawing questions should only have one correct answer.</p>}
                        {values.answers.map(answer => {
                            if (answer.imageExists) {
                                const answerImagePath = answer.image?.update ? getImagePath(moduleName, type, assessmentNumber, question.number, answer.image?.letter) : null
                                return (
                                    <div className="my-2">
                                        <div className="d-flex justify-content-between">
                                            <p className="my-0 ms-1 p-0 text-primary">{answer.letter}</p>
                                            <p role="button" onClick={() => deleteAnswer(answer.letter)}><i className={`fas fa-x text-danger`}/> remove</p>
                                        </div>
                                        {answerImagePath && <Image src={answerImagePath} alt={answer.image?.altText} className="rounded mb-2" fluid />}
                                        <Form.Control 
                                            type="file" 
                                            className="mb-2"
                                            accept=".png,image/png,.jpg,.jpeg,image/jpeg"
                                            onChange={(e) => onChangeImageAnswer(e.target as HTMLInputElement, answer.letter)} />
                                        <Form.Control 
                                            type="text" 
                                            name="altText" 
                                            placeholder="Alt text - brief description for screen readers" 
                                            value={answer.image?.altText ?? ""}
                                            onChange={(e) => onChangeImageAnswerAltText(e.target as HTMLInputElement, answer.letter)} />
                                        <Form.Group controlId="answer-explanation" className="my-4 text-start fw-bold">
                                            <Form.Label className="fw-bold h4">Explanation</Form.Label>
                                            <Form.Control as="textarea" value={answer.explanation} rows={7} onChange={(e) => onChangeAnswerExplanation(e, answer.letter)} />
                                        </Form.Group>

                                    </div>
                                )
                            } else {
                                return (
                                    <div className="my-2">
                                        <div className="d-flex justify-content-between">
                                            <p className="my-0 ms-1 p-0 text-primary">{answer.letter}</p>
                                            <p role="button" onClick={() => deleteAnswer(answer.letter)}><i className={`fas fa-x text-danger`}/> remove</p>
                                        </div>
                                        <Form.Control type="text" name="answers" value={answer.answer} onChange={(e) => onChangeTextAnswer(e, answer.letter)} />
                                        <Form.Group controlId="answer-explanation" className="my-4 text-start fw-bold">
                                            <Form.Label className="fw-bold h4">Explanation</Form.Label>
                                            <Form.Control as="textarea" value={answer.explanation} rows={7} onChange={(e) => onChangeAnswerExplanation(e, answer.letter)} />
                                        </Form.Group>
                                    </div>
                                )
                            }
                        })}
                        <div className="d-flex justify-content-center">
                            <Button variant="primary" className="me-1" onClick={() => addNewAnswer("image")}>Add Image</Button>
                            <Button variant="primary" onClick={() => addNewAnswer("text")}>Add Text</Button>
                        </div>
                    </Form.Group>

                    <Form.Group controlId="explanation" className="my-4 text-start fw-bold">
                        <Form.Label className="fw-bold h4">Correct Answer Explanation</Form.Label>
                        <Form.Control as="textarea" name="explanation" value={values.explanation} rows={7} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group controlId="standards" className="my-4 text-start">
                        <Form.Label className="fw-bold h4">
                            Standards
                        </Form.Label>
                        <SelectStandards assessmentQuestionId={question.id} setFieldValue={setFieldValue} />
                    </Form.Group>

                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    {submitting && <BeatLoader />}
                    {!submitting && <Button variant="primary" type="submit" className="my-3" disabled={!formik.dirty} onClick={() => formik.handleSubmit()}>Update</Button>}
                </Modal.Footer>
            </Modal>
        </Form>
    </FormikProvider>
  )
}

export default EditAssessmentQuestion
